/**
 * Utility functions for serializing and deserializing search parameters to/from URL query strings
 */

export interface SearchParams {
  originId: string;
  destinationId: string;
  originName: string;
  destinationName: string;
  date: string;
  tripType: 'departure' | 'arrival';
  options: Record<string, any>;
}

/**
 * Serialize search parameters to URLSearchParams
 */
export const serializeSearchParams = (
  originId: string,
  destinationId: string,
  originName: string,
  destinationName: string,
  date: string,
  tripType: 'departure' | 'arrival',
  options: Record<string, any>
): URLSearchParams => {
  const params = new URLSearchParams();
  
  if (originId) params.set('from', originId);
  if (destinationId) params.set('to', destinationId);
  if (originName) params.set('fromName', encodeURIComponent(originName));
  if (destinationName) params.set('toName', encodeURIComponent(destinationName));
  if (date) params.set('date', date);
  if (tripType) params.set('tripType', tripType);
  
  // Serialize options object as JSON
  if (options && Object.keys(options).length > 0) {
    params.set('options', encodeURIComponent(JSON.stringify(options)));
  }
  
  return params;
};

/**
 * Deserialize URLSearchParams to search parameters
 */
export const deserializeSearchParams = (searchParams: URLSearchParams): SearchParams => {
  const originId = searchParams.get('from') || '';
  const destinationId = searchParams.get('to') || '';
  const originName = searchParams.get('fromName') ? decodeURIComponent(searchParams.get('fromName')!) : '';
  const destinationName = searchParams.get('toName') ? decodeURIComponent(searchParams.get('toName')!) : '';
  const date = searchParams.get('date') || '';
  const tripType = (searchParams.get('tripType') as 'departure' | 'arrival') || 'departure';
  
  let options: Record<string, any> = {};
  const optionsParam = searchParams.get('options');
  if (optionsParam) {
    try {
      options = JSON.parse(decodeURIComponent(optionsParam));
    } catch (e) {
      console.error('Failed to parse options from URL:', e);
      options = {};
    }
  }
  
  return { originId, destinationId, originName, destinationName, date, tripType, options };
};
